import { ref, computed, watch, type ComputedRef } from 'vue';
import type Product from '@monolith/legacy/types/product';
import type { ESProductHit } from '@bc/discovery/types';
import { isProductOutOfStock } from '@monolith/legacy/services/product-variants';
import { debounce } from '@core/utilities/lodash-utils';
import { useCart } from '@monolith/legacy/modules/cart-2/composables/use-cart';
import { useCartStore } from './use-cart-store';

export type AddToCartMode = 'add' | 'update';

export type AddPayload = {
  brand: unknown;
  option: unknown;
  variant: unknown;
  quantity: number; // in units (items ÷ unit_multiplier)
};

export function useAddToCart(
  product: ComputedRef<Product | ESProductHit>,
  mode: ComputedRef<AddToCartMode> = computed(() => 'add' as AddToCartMode),
  presetQuantity: boolean = false
) {
  const store = useCartStore();
  const { updateItemQuantity, ensureCart } = useCart();

  const getProduct = () => product.value;

  const quantity = ref(0);
  const confirmation = ref(false);
  const isLoading = ref(false);

  const isMultiVariant = computed(() => (getProduct().variants?.length ?? 0) > 1);
  const isOutOfStock = computed(() => isProductOutOfStock(getProduct()));
  // Handle both ESProductHit (unit_multiplier) and ProductOutputData (unitMultiplier)
  const unitMultiplier = computed(() => (getProduct().unit_multiplier ?? (getProduct() as { unitMultiplier?: number }).unitMultiplier) ?? 1);

  const initQuantity = () => {
    if (mode.value === 'update') {
      const option = getProduct().options?.[0];
      const cartItem = option ? store.itemByOptionId.value(option.id) : null;
      quantity.value = (cartItem?.quantity ?? 0) * unitMultiplier.value;
    } else {
      quantity.value = presetQuantity ? unitMultiplier.value : 0;
    }
  };
  initQuantity();

  let confirmationTimer: ReturnType<typeof setTimeout> | null = null;

  const showConfirmation = () => {
    confirmation.value = true;
    confirmationTimer = setTimeout(() => {
      confirmation.value = false;
      quantity.value = presetQuantity ? unitMultiplier.value : 0;
      confirmationTimer = null;
    }, 2000);
  };

  const addToCart = async (openVatModal: () => void): Promise<AddPayload | null> => {
    if (!store.canGetWholesalePrice.value) {
      openVatModal();
      return null;
    }
    if (isLoading.value) {return null;}

    isLoading.value = true;
    try {
      const variant = getProduct().variants?.[0] ?? null;
      const units = quantity.value / unitMultiplier.value;

      if (variant?.uuid) {
        await ensureCart();
        await updateItemQuantity(variant.uuid, units, true); // additive
      }

      showConfirmation();

      const option = getProduct().options?.find((o) => o.sku === variant?.sku) ?? getProduct().options?.[0];
      return { brand: getProduct().brand, option, variant, quantity: units };
    } finally {
      isLoading.value = false;
    }
  };

  const debouncedUpdate = debounce(async () => {
    if (!store.canGetWholesalePrice.value) {return;}
    const variant = getProduct().variants?.[0] ?? null;
    if (!variant?.uuid) {return;}
    const units = quantity.value / unitMultiplier.value;
    await ensureCart();
    await updateItemQuantity(variant.uuid, units, false); // absolute (replace)
  }, 500);

  watch(quantity, () => {
    if (mode.value === 'update') {debouncedUpdate();}
  });

  const cleanup = () => {
    if (confirmationTimer) {clearTimeout(confirmationTimer);}
    debouncedUpdate.cancel();
  };

  return {
    quantity,
    confirmation,
    isLoading,
    isMultiVariant,
    isOutOfStock,
    unitMultiplier,
    userIsRetailer: store.userIsRetailer,
    userIsBrand: store.userIsBrand,
    canGetWholesalePrice: store.canGetWholesalePrice,
    addToCart,
    cleanup,
  };
}
